import { useState, useEffect, useCallback, useRef } from 'react';
import { ambientAudioService } from '../services/AmbientAudioService';
import apiUrls from '../api/apiUrls';
import fetchWithAuth from '../api/fetchWithAuth';

const STORAGE_KEY = 'readerum_ambient_config';

export const useAmbientAudio = (initialPreferences = null) => {
  const [tracks, setTracks] = useState(() => ambientAudioService.getTracks());
  
  // Helper para obtener preferencias soportando camelCase y snake_case
  const getPref = (prefs, key) => {
    if (!prefs) return undefined;
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
    return prefs[key] !== undefined ? prefs[key] : prefs[snakeKey];
  };

  const [config, setConfig] = useState(() => {
    const pTrackId = getPref(initialPreferences, 'ambientTrackId');
    const pVolume = getPref(initialPreferences, 'ambientVolume');

    // Prioridad 1: Preferencias de la API (si vienen)
    if (pTrackId !== undefined) {
      return { 
        trackId: pTrackId, 
        volume: parseFloat(pVolume) || 0.5 
      };
    }
    // Prioridad 2: LocalStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : { trackId: null, volume: 0.5 };
  });

  const [isPlaying, setIsPlaying] = useState(() => ambientAudioService.isPlaying());
  const lastInitialPrefsRef = useRef(initialPreferences);

  // 1. Cargar pistas (si no están ya en caché)
  useEffect(() => {
    const initTracks = async () => {
      const availableTracks = await ambientAudioService.loadTracks(fetchWithAuth, apiUrls);
      setTracks(availableTracks);
      
      // Si tenemos un track seleccionado pero no está cargado en el service, cargarlo
      if (config.trackId && availableTracks.length > 0) {
        const track = availableTracks.find(t => t.id === config.trackId);
        if (track) {
          const fullUrl = track.url.startsWith('http') ? track.url : `${apiUrls.ambientFiles}${track.url.split('/').pop()}`;
          ambientAudioService.setTrack(fullUrl, track.id);
          ambientAudioService.setVolume(config.volume);
        }
      }
    };
    initTracks();
  }, []);

  // 2. Suscribirse a cambios en el servicio global
  useEffect(() => {
    const unsubscribe = ambientAudioService.subscribe((service) => {
      setIsPlaying(service.isPlaying());
      setTracks(service.getTracks());
      setConfig(prev => {
        // Evitar actualizaciones de estado si los valores no han cambiado realmente
        if (prev.trackId === service.currentTrackId && prev.volume === service.audio.volume) {
          return prev;
        }
        return {
          ...prev,
          trackId: service.currentTrackId,
          volume: service.audio.volume
        };
      });
    });
    return unsubscribe;
  }, []);

  // 3. Sincronizar Service cuando cambian las preferencias externas (prop)
  // Solo lo hacemos si la referencia de initialPreferences cambia realmente
  useEffect(() => {
    if (initialPreferences && initialPreferences !== lastInitialPrefsRef.current) {
      const pTrackId = getPref(initialPreferences, 'ambientTrackId');
      const pVolume = getPref(initialPreferences, 'ambientVolume');

      if (pTrackId !== undefined && pTrackId !== config.trackId) {
        const track = tracks.find(t => t.id === pTrackId);
        if (track) {
          const fullUrl = track.url.startsWith('http') ? track.url : `${apiUrls.ambientFiles}${track.url.split('/').pop()}`;
          ambientAudioService.setTrack(fullUrl, track.id);
        } else if (pTrackId === null) {
          ambientAudioService.stop();
        }
      }

      if (pVolume !== undefined && parseFloat(pVolume) !== config.volume) {
        ambientAudioService.setVolume(pVolume);
      }

      lastInitialPrefsRef.current = initialPreferences;
    }
  }, [initialPreferences, tracks, config.trackId, config.volume]);

  // Sincronizar Service con el volumen y persistir localmente
  useEffect(() => {
    ambientAudioService.setVolume(config.volume);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [config.volume, config.trackId]);

  const selectTrack = useCallback((trackId) => {
    if (trackId === null) {
      ambientAudioService.stop();
      setConfig(prev => ({ ...prev, trackId: null }));
      setIsPlaying(false);
      return;
    }

    const track = tracks.find(t => t.id === trackId);
    if (track) {
      const fullUrl = track.url.startsWith('http') ? track.url : `${apiUrls.ambientFiles}${track.url.split('/').pop()}`;
      ambientAudioService.setTrack(fullUrl, track.id);
      setConfig(prev => ({ ...prev, trackId }));
      ambientAudioService.play();
      setIsPlaying(true);
    }
  }, [tracks]);

  const togglePlay = useCallback(() => {
    if (ambientAudioService.isPlaying()) {
      ambientAudioService.pause();
    } else {
      ambientAudioService.play();
    }
  }, []);

  const playAmbient = useCallback(() => {
    ambientAudioService.play();
  }, []);

  const pauseAmbient = useCallback(() => {
    ambientAudioService.pause();
  }, []);

  const updateVolume = useCallback((volume) => {
    setConfig(prev => ({ ...prev, volume }));
  }, []);

  return {
    currentTrackId: config.trackId,
    volume: config.volume,
    isPlaying,
    tracks,
    selectTrack,
    togglePlay,
    playAmbient,
    pauseAmbient,
    updateVolume
  };
};
